import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdCheckCircle,
  MdRadioButtonUnchecked,
} from "react-icons/md";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * ChoiceCard - a selectable tile (toggle button) for picking one or more options.
 * Selection is shown by border, tint and a check/radio indicator, and exposed as aria-pressed.
 *
 * @param {object} props
 * @param {boolean} props.selected
 * @param {() => void} props.onClick
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.description]
 * @param {React.ComponentType} [props.icon] Leading icon component.
 * @param {boolean} [props.multiple=false] Checkbox indicator instead of a radio indicator.
 * @param {boolean} [props.disabled=false]
 * @param {React.ReactNode} [props.children] Extra content under the description (e.g. a Badge).
 * @param {string} [props.className]
 */
const ChoiceCard = ({
  selected,
  onClick,
  title,
  description,
  icon: Icon,
  multiple = false,
  disabled = false,
  children,
  className,
}) => {
  const Indicator = multiple
    ? selected
      ? MdCheckBox
      : MdCheckBoxOutlineBlank
    : selected
    ? MdCheckCircle
    : MdRadioButtonUnchecked;

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "flex w-full flex-col items-start rounded-control border-2 p-4 text-left transition-colors focus-ring disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-primary bg-brand-50 dark:bg-brand-900/20"
          : "border-line bg-surface enabled:hover:border-fg-muted/40",
        className
      )}
    >
      <span className="flex w-full items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {Icon && (
            <Icon
              aria-hidden="true"
              className={cx(
                "h-5 w-5 shrink-0",
                selected ? "text-primary dark:text-accent" : "text-fg-muted"
              )}
            />
          )}
          <span className="text-sm font-semibold text-fg">{title}</span>
        </span>
        <Indicator
          aria-hidden="true"
          className={cx(
            "h-5 w-5 shrink-0",
            selected ? "text-primary dark:text-accent" : "text-fg-muted"
          )}
        />
      </span>
      {description && (
        <span className="mt-1 text-xs text-fg-muted">{description}</span>
      )}
      {children}
    </button>
  );
};

export default ChoiceCard;
